import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "../supabase/supabase-client";
import { LogeMemberRole, Role } from "../types/entities";

// Roster rows nobody has signed up with yet — the pick-list on /signup and
// the "afventer login" tag on the admin member list.
export const useUnclaimedMembers = () => {
  return useQuery({
    queryKey: ["members", "unclaimed"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("loge_members")
        .select("*")
        .is("auth_user_id", null);
      if (error) throw error;
      return data;
    },
  });
};

export interface AddMemberPayload {
  name: string;
  emoji: string;
}

export const useAddMember = () => {
  return useMutation(async (payload: AddMemberPayload) => {
    const { error } = await supabase.from("loge_members").insert(payload);
    if (error) throw error;
  });
};

export interface UpdateMemberPayload {
  memberId: number;
  name?: string;
  emoji?: string;
}

export const useUpdateMember = () => {
  return useMutation(async ({ memberId, ...updates }: UpdateMemberPayload) => {
    const { error } = await supabase
      .from("loge_members")
      .update(updates)
      .eq("id", memberId);
    if (error) throw error;
  });
};

// Lets a member change their own avatar emoji. Goes through the
// update_own_emoji() function (see the matching migration) rather than a
// plain table update, since the self-edit RLS policy on loge_members only
// covers claiming an unclaimed row, not editing an already-claimed one.
export const useUpdateOwnEmoji = () => {
  return useMutation(async (emoji: string) => {
    const { error } = await supabase.rpc("update_own_emoji", { new_emoji: emoji });
    if (error) throw error;
  });
};

// Stamps the caller's roster row with "now" so admins can see who's actually
// using the app. Fired on session load (see SupabaseContext), not on every
// request — good enough for a "last active" subtitle.
export const touchLastActive = async () => {
  const { error } = await supabase.rpc("touch_last_active");
  if (error) throw error;
};

// Revokes login access (unlinks the auth account) but keeps the roster row
// and its history (votes, fines) intact — a hard delete would orphan those.
export const useRevokeMemberAccess = () => {
  return useMutation(async (memberId: number) => {
    const { error } = await supabase
      .from("loge_members")
      .update({ auth_user_id: null, email: null })
      .eq("id", memberId);
    if (error) throw error;
  });
};

// Small table (a handful of rows) — fetched whole and grouped client-side,
// same staleTime-Infinity-ish pattern as useLogeMembers in user-api.ts.
export const useAllMemberRoles = () => {
  return useQuery({
    queryKey: ["member_roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("loge_member_roles")
        .select("*");
      if (error) throw error;
      return data as LogeMemberRole[];
    },
  });
};

export const useMemberStats = (memberId?: number) => {
  return useQuery({
    queryKey: ["member_stats", memberId],
    queryFn: async () => {
      const id = memberId as number;
      const [participants, votes, unpaidFines] = await Promise.all([
        supabase.from("loge_event_participants").select("loge_event_id").eq("user_id", id),
        supabase
          .from("votes")
          .select("id", { count: "exact", head: true })
          .eq("user_id", id)
          .eq("deleted", false),
        supabase
          .from("fines")
          .select("id", { count: "exact", head: true })
          .eq("loge_member_id", id)
          .eq("status", "unpaid")
          .eq("deleted", false),
      ]);
      if (participants.error) throw participants.error;
      if (votes.error) throw votes.error;
      if (unpaidFines.error) throw unpaidFines.error;

      const eventsAttended = new Set(
        (participants.data ?? []).map((row) => row.loge_event_id),
      ).size;

      return {
        eventsAttended,
        votesCast: votes.count ?? 0,
        unpaidFines: unpaidFines.count ?? 0,
      };
    },
    enabled: !!memberId,
  });
};

export interface SetMemberRolesPayload {
  memberId: number;
  roles: Role[];
}

// Replaces a member's elevated roles wholesale — simplest correct approach
// for a table this small (a handful of rows total across the whole roster).
export const useSetMemberRoles = () => {
  return useMutation(async ({ memberId, roles }: SetMemberRolesPayload) => {
    const { error: deleteError } = await supabase
      .from("loge_member_roles")
      .delete()
      .eq("loge_member_id", memberId);
    if (deleteError) throw deleteError;

    if (roles.length === 0) return;

    const { error: insertError } = await supabase
      .from("loge_member_roles")
      .insert(roles.map((role) => ({ loge_member_id: memberId, role })));
    if (insertError) throw insertError;
  });
};
